import http from './http.service'
import localStorageService from './local-storage.service'
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import { KIOSK_ACTIVATION_KEY, KIOSK_SESSION_KEY, KIOSK_TOKEN_KEY, KIOSK_BROWSER_ID_KEY } from './keys'
import { KioskActivation, CreatePrintLogPayload, PrintQueueStatus, CompanyFont } from './kiosk.types'

export { KIOSK_ACTIVATION_KEY, KIOSK_SESSION_KEY, KIOSK_TOKEN_KEY, KIOSK_BROWSER_ID_KEY }

export interface PrinterCodeResult {
  event: Record<string, any> | null
  code: Record<string, any> | null
  admin: Record<string, any> | null
}

class KioskService {
  async findCode(code: string, stationId: string): Promise<PrinterCodeResult> {
    const response = await http.get<PrinterCodeResult>({
      url: '/printer-code',
      requireToken: false,
      config: { params: { code } },
    })
    localStorageService.setItem(KIOSK_ACTIVATION_KEY, { code: response.data.code, event: response.data.event, workstation: stationId })
    return response.data
  }

  async loginKioskCode(eventUuid: string, code: string, stationId: string): Promise<any> {
    const browserId = await this.getBrowserId()
    const response = await http.post<any>({
      url: `/event/${eventUuid}/kiosk/session`,
      requireToken: false,
      payload: { code, machine_id: stationId, browser_id: browserId },
    })
    localStorageService.setItem(KIOSK_SESSION_KEY, response.data)
    const token = response.data?.access_token
    if (token) {
      localStorageService.setItem(KIOSK_TOKEN_KEY, token)
    }
    return response.data
  }

  /** Verifies the rotating Kiosk Admin PIN. Public endpoint: no token. Returns a short-lived single-use deactivation token. */
  async verifyAdminPin(pin: string): Promise<{ deactivation_token: string; expires_in: number }> {
    const browserId = await this.getBrowserId()
    const response = await http.post<{ deactivation_token: string; expires_in: number }>({
      url: '/kiosk/admin-pin/verify',
      requireToken: false,
      payload: { pin, browser_id: browserId },
    })
    return response.data
  }

  /** Deactivates this kiosk on the backend. skipDeactivationEvent keeps the legacy "Kiosk Deactivated" dialog from popping mid-flow. */
  async deactivateSelf(eventUuid: string, deactivationToken: string): Promise<void> {
    await http.post({
      url: `/event/${eventUuid}/kiosk/deactivate`,
      requireToken: true,
      payload: { deactivation_token: deactivationToken },
      config: { skipDeactivationEvent: true },
    })
  }

  async getKioskSettings(event_uuid: string): Promise<any> {
    const response = await http.get<any>({
      url: `/event/${event_uuid}/kiosk-settings`,
      requireToken: true,
    })
    return response.data
  }

  async getCompanyFonts(company_uuid: string): Promise<CompanyFont[]> {
    const response = await http.get<CompanyFont[]>({
      url: `/company/${company_uuid}/fonts`,
      requireToken: true,
    })
    return response.data
  }

  async searchParticipants(event_uuid: string, params: Record<string, string>): Promise<any> {
    const response = await http.get({
      url: `/event/${event_uuid}/participants-simple`,
      requireToken: true,
      config: { params },
    }, event_uuid)
    return response
  }

  async getAttendeeBadgeSVG(event_uuid: string, participant_uuid: string): Promise<any> {
    const response = await http.get({
      url: `/event/${event_uuid}/badge/${participant_uuid}`,
      requireToken: true,
    })
    return response
  }

  async getParticipantPrintCount(participant_uuid: string): Promise<{ count: number }> {
    const { eventUuid } = this.getPrintLogContext()
    if (!eventUuid) return { count: 0 }
    const response = await http.get<{ count: number }>({
      url: `/event/${eventUuid}/print-queue/participant/${participant_uuid}/count`,
      requireToken: true,
    })
    return response.data
  }

  private readonly STRIP_FIELDS = new Set([
    'event_id', 'event_uuid', 'registration_uuid', 'deleted_at', 'created_at',
    'updated_at', 'modified_date', 'create_date', 'date_registered',
    'selected_options_kv', 'selected_options_printable', 'promo_code_selection', 'promo_code_selections',
  ])

  async saveParticipant(event_uuid: string, participant_uuid: string, participant_data: Record<string, any>, badge_field_settings: any[]): Promise<void> {
    const payload: Record<string, any> = {}
    for (const [key, value] of Object.entries(participant_data)) {
      if (!this.STRIP_FIELDS.has(key)) {
        payload[key] = value
      }
    }
    payload.badge_field_settings = badge_field_settings

    await http.patch({
      url: `/event/${event_uuid}/participant/${participant_uuid}`,
      requireToken: true,
      payload,
      config: {
        params: {
          singleParticipantEdit: 1,
          send_email_override: false,
          send_cancel_email: false,
          mark_group_members_completed: false,
        },
      },
    })
  }

  private getPrintLogContext(): { eventUuid: string | null; workstation: string | null; user: string | null; source: string | null } {
    const activation = localStorageService.getItem<KioskActivation>(KIOSK_ACTIVATION_KEY)
    const session = localStorageService.getItem<any>(KIOSK_SESSION_KEY)
    const source = localStorageService.getItem<string>(KIOSK_BROWSER_ID_KEY)
    return {
      eventUuid: activation?.event?.uuid ?? null,
      workstation: activation?.workstation ?? null,
      user: session?.user?.uuid ?? null,
      source: source ?? null,
    }
  }

  async postParticipantSimple(participant_uuid: string, printDate: string): Promise<void> {
    const { eventUuid } = this.getPrintLogContext()
    if (!eventUuid) return
    await http.post({
      url: `/event/${eventUuid}/participants-simple`,
      requireToken: true,
      payload: { id: participant_uuid, badge_print_date: printDate },
    })
  }

  async createPrintLog(participant_uuid: string, printDate: string, status: PrintQueueStatus = 'PRINTED'): Promise<any> {
    const { eventUuid, workstation, user, source } = this.getPrintLogContext()
    const payload: CreatePrintLogPayload = {
      participant_uuid,
      printed_time: printDate,
      status,
      properties: { source, workstation, participant_uuid, user },
    }
    return http.post({
      url: `/event/${eventUuid}/print-queue`,
      requireToken: true,
      payload,
    })
  }

  async updatePrintLog(log_uuid: string, data: Partial<CreatePrintLogPayload>): Promise<any> {
    const { eventUuid } = this.getPrintLogContext()
    return http.patch({
      url: `/event/${eventUuid}/print-queue/${log_uuid}`,
      requireToken: true,
      payload: data,
    })
  }

  async clearBrowserId(): Promise<void> {
    localStorageService.removeItem(KIOSK_BROWSER_ID_KEY)
    document.cookie = `${KIOSK_BROWSER_ID_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Strict`
    await this.deleteFromIDB(KIOSK_BROWSER_ID_KEY)
  }

  private async getBrowserId(): Promise<string> {
    const storage = localStorageService

    const fromLS = storage.getItem<string>(KIOSK_BROWSER_ID_KEY)
    if (fromLS) { await this.syncBrowserId(fromLS); return fromLS }

    const fromCookie = this.readCookie(KIOSK_BROWSER_ID_KEY)
    if (fromCookie) { await this.syncBrowserId(fromCookie); return fromCookie }

    const fromIDB = await this.readFromIDB(KIOSK_BROWSER_ID_KEY)
    if (fromIDB) { await this.syncBrowserId(fromIDB); return fromIDB }

    const fp = await FingerprintJS.load()
    const { visitorId } = await fp.get()
    await this.syncBrowserId(visitorId)
    return visitorId
  }

  private async syncBrowserId(id: string): Promise<void> {
    localStorageService.setItem(KIOSK_BROWSER_ID_KEY, id)
    this.writeCookie(KIOSK_BROWSER_ID_KEY, id)
    await this.writeToIDB(KIOSK_BROWSER_ID_KEY, id)
  }

  private readCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : null
  }

  private writeCookie(name: string, value: string): void {
    const expires = new Date()
    expires.setFullYear(expires.getFullYear() + 10)
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Strict`
  }

  private readonly IDB_NAME = 'kiosk_db'
  private readonly IDB_STORE = 'kiosk'

  private openIDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(this.IDB_NAME, 1)
      req.onupgradeneeded = (e) => {
        (e.target as IDBOpenDBRequest).result.createObjectStore(this.IDB_STORE)
      }
      req.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result)
      req.onerror = () => reject(req.error)
    })
  }

  private async readFromIDB(key: string): Promise<string | null> {
    try {
      const db = await this.openIDB()
      return new Promise((resolve) => {
        const req = db.transaction(this.IDB_STORE, 'readonly').objectStore(this.IDB_STORE).get(key)
        req.onsuccess = () => resolve(req.result ?? null)
        req.onerror = () => resolve(null)
      })
    } catch { return null }
  }

  private async writeToIDB(key: string, value: string): Promise<void> {
    try {
      const db = await this.openIDB()
      await new Promise<void>((resolve) => {
        const tx = db.transaction(this.IDB_STORE, 'readwrite')
        tx.objectStore(this.IDB_STORE).put(value, key)
        tx.oncomplete = () => resolve()
        tx.onerror = () => resolve()
      })
    } catch { /* non-fatal */ }
  }

  private async deleteFromIDB(key: string): Promise<void> {
    try {
      const db = await this.openIDB()
      await new Promise<void>((resolve) => {
        const tx = db.transaction(this.IDB_STORE, 'readwrite')
        tx.objectStore(this.IDB_STORE).delete(key)
        tx.oncomplete = () => resolve()
        tx.onerror = () => resolve()
      })
    } catch { /* non-fatal */ }
  }
}

export default new KioskService()
